import apiClient from '../../Services/ApiClient';
import { useState } from 'react';
import { useAppContext } from '../../Services/AppContext';
import UserAvatar from '../UserAvatar/UserAvatar';
import FormattedDate from '../FormattedDate';
import NoEscape from '../NoEscape';
import { USER_FULL_INFO, USER_VZP_DOCTORS } from '../UserInfo/UserData';
import Loading from '../Loading';
import { useEffectAsync } from '../../helpers';
import PatientProfileButton from './PatientProfileButton';
import PatientProfileHealthCard from './PatientProfileHealthCard';
import { useLoadProfile } from './PatientProfileDataLoader';

export default function PatientProfile({
	userId,
	showIdentifier = false,
	showInsurance = false,
	showVerifyInformation = false,
	showPsychoQuestionnaire = false,
	showHealthCard = false,
	links = {},
}) {
	const [showMore, setShowMore] = useState(null);
	const [showedHealthCard, setShowedHealthCard] = useState(null);

	const { user, psychoQuestionnaire, psychoQuestionnaireTheme, doctorsData } =
		useLoadProfile({
			userId,
			loadPsychoQuestionnaire: showPsychoQuestionnaire,
			loadVerifyInformation: showVerifyInformation,
		});

	const { translation } = useAppContext();

	if (!user) {
		return (
			<div className={'py-5'}>
				<Loading />
			</div>
		);
	}

	const insuranceCompany = user.profile?.insuranceCompanyTermItem;
	const vzpResultIconClass = user.vzpResult?.result
		? 'fa-green fa-check-circle'
		: 'fa-times-circle text-danger';
	const csszResultIconClass = user.csszResult?.result
		? 'fa-green fa-check-circle'
		: 'fa-times-circle text-danger';

	return (
		<main>
			<div className='caseAcceptedText radius-right bg-white p-relative-1 drop-shadow'>
				<div className='row'>
					<div className='col-12 col-md-9 py-4 px-4'>
						<table
							style={{ width: '100%' }}
							className='font-weight-light font-size-22'>
							<tbody>
								{user.profile?.birthDate && (
									<tr>
										<td style={{ width: '50px' }}>
											<i className='fa fa-birthday-cake fa-fw ml-2'></i>
										</td>
										<td>
											<FormattedDate date={user.profile.birthDate} />
											{showIdentifier && (
												<>
													{' '}
													<strong>
														{translation['base.shared.birthNumberShort']}
													</strong>
													: {user?.profile?.birthNumber}
												</>
											)}
										</td>
									</tr>
								)}
								{(links['mouNumber'] || false) && (
									<tr>
										<td style={{ width: '50px' }}>
											<i className='fa fa-info-circle fa-fw ml-2'></i>
										</td>
										<td>
											<strong>{translation['base.shared.mouNumber']}:</strong>{' '}
											{user.partnerIdentifier || '-'}{' '}
											<a href={links['mouNumber']}>
												<i className='fa fa-print fa-fw'></i>{' '}
												<span className='text-underline'>
													{translation['base.shared.form.send']}
												</span>
											</a>
										</td>
									</tr>
								)}
								{user.profile?.address?.formattedFullAddress && (
									<tr>
										<td valign='top'>
											<i className='fa fa-home fa-fw ml-2'></i>
										</td>
										<td>
											<NoEscape
												children={user.profile.address.formattedFullAddress}
											/>
										</td>
									</tr>
								)}
								{user.formattedPhoneNumber && (
									<tr>
										<td>
											<i className='fa fa-mobile-alt fa-fw ml-2'></i>
										</td>
										<td>{user.formattedPhoneNumber}</td>
									</tr>
								)}
								{showInsurance &&
									(insuranceCompany || user.profile?.insuranceNumber) && (
										<tr>
											<td valign='top'>
												<i className='fa fa-info-circle fa-fw ml-2'></i>
											</td>
											<td>
												{insuranceCompany &&
													insuranceCompany.value2 +
														' - ' +
														insuranceCompany.value}
												{insuranceCompany && user.profile?.insuranceNumber && (
													<br />
												)}
												{translation['base.shared.insuranceNumber']}:{' '}
												{user.profile.insuranceNumber}
											</td>
										</tr>
									)}
								{user.isChild && user?.parents?.length && (
									<>
										{user.parents.map((parentUser, index) => {
											return (
												<tr key={index}>
													<td />
													<td>
														<strong>
															{translation['base.shared.legalRepresentative']}
														</strong>
														: {parentUser.profile.fullName}
													</td>
												</tr>
											);
										})}
									</>
								)}
								{user.csszResult && (
									<tr>
										<td valign='top'>
											<i className={'fa fa-fw ml-2 ' + csszResultIconClass}></i>
										</td>
										<td>
											{user.csszResult?.result
												? translation['component.csszResult.addressVerified']
												: translation[
														'component.csszResult.addressNotVerified'
												  ]}{' '}
											- <FormattedDate date={user.csszResult.checkedAt} />
										</td>
									</tr>
								)}
								{user.vzpResult && (
									<tr>
										<td valign='top'>
											<i className={'fa fa-fw ml-2 ' + vzpResultIconClass}></i>
										</td>
										<td>
											{user.vzpResult?.result
												? translation['component.vzpResult.insuranceVerified']
												: translation[
														'component.vzpResult.insuranceNotVerified'
												  ]}{' '}
											- <FormattedDate date={user.vzpResult.checkedAt} />
										</td>
									</tr>
								)}
								{showPsychoQuestionnaire && psychoQuestionnaire && (
									<tr>
										<td valign='top'>
											<i
												className={
													'fa fa-' +
													psychoQuestionnaire?.fontIcon +
													' fa-fw ml-2 text-' +
													psychoQuestionnaireTheme
												}></i>
										</td>
										<td>
											<strong className={'text-' + psychoQuestionnaireTheme}>
												{psychoQuestionnaire?.title || ''}
											</strong>
											<br />
											<NoEscape
												children={psychoQuestionnaire?.description || ''}
											/>
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>
					<div className='col-12 col-md-3 pt-4'>
						<UserAvatar
							userId={user.id}
							type={'background'}
							customClass={
								'profile-photo profile-photo__patientBadgeProfilePhoto'
							}
						/>
					</div>
				</div>
			</div>
			<div className='bg-white inserted-radius p-5'>
				<div className='row'>
					{(links['documentation'] || false) && (
						<PatientProfileButton
							text={translation['base.eCard']}
							link={links['documentation']}
						/>
					)}
					{(links['notes'] || false) && (
						<PatientProfileButton
							text={translation['base.shared.internalNotes']}
							link={links['notes']}
						/>
					)}
					{(links['contactPatient'] || false) && (
						<PatientProfileButton
							text={translation['base.shared.contactAPatient']}
							link={links['contactPatient']}
							theme={'green'}
						/>
					)}
					{(links['questionnaire'] || false) && (
						<PatientProfileButton
							text={translation['base.shared.quiestionnaire']}
							link={links['questionnaire']}
						/>
					)}
					{(links['events'] || false) && (
						<PatientProfileButton
							text={translation['base.shared.patientsOrders']}
							link={links['events']}
						/>
					)}
					{(links['educationMaterials'] || false) && (
						<PatientProfileButton
							text={translation['base.shared.educationMaterials']}
							link={links['educationMaterials']}
						/>
					)}
					{(links['verifyInsurance'] || false) && (
						<PatientProfileButton
							text={translation['base.verifyInsurance']}
							link={links['verifyInsurance']}
						/>
					)}
					{(links['verifyAddress'] || false) && (
						<PatientProfileButton
							text={translation['base.verifyAddress']}
							link={links['verifyAddress']}
						/>
					)}
					{(links['verifyRegisteringDoctors'] || false) && (
						<PatientProfileButton
							text={translation['base.registerinDoctors']}
							link={links['verifyRegisteringDoctors']}
						/>
					)}
					{(links['issueEprescription'] || false) && (
						<PatientProfileButton
							text={translation['base.issueEprescription']}
							link={links['issueEprescription']}
						/>
					)}
					{(links['medicalRecords'] || false) && (
						<PatientProfileButton
							text={translation['base.searchMedicalRecord']}
							link={links['medicalRecords']}
						/>
					)}
					{(links['medicalRecordsVaccination'] || false) && (
						<PatientProfileButton
							text={translation['base.searchVaccination']}
							link={links['medicalRecordsVaccination']}
						/>
					)}
				</div>
				{showVerifyInformation && (
					<div>
						<a
							className='patient-card-detail__show_anamnestic'
							onClick={() => setShowMore(!showMore)}>
							{translation['base.shared.showMoreInfo']}
						</a>
						<div className={'collapse ' + (showMore ? 'show' : null)}>
							<div className='row'>
								<div className='col-12'>
									<strong className='text-primary'>
										{translation['base.registerinDoctors']}
									</strong>
									<br />
									{doctorsData && doctorsData.length ? (
										<div className='d-flex'>
											{doctorsData.map((item, index) => {
												return (
													<div
														className='mt-3 pb-5 text-gray font-size-19'
														key={index}>
														<span className='font-weight-semibold'>
															Pojišťovna:
														</span>{' '}
														{item.insuranceCompany &&
															item.insuranceCompany.code +
																' - ' +
																item.insuranceCompany.value}
														<br />
														<span className='font-weight-semibold'>
															Odbornost:
														</span>{' '}
														{item.expertise &&
															item.expertise.name + ' ' + item.expertise.code}
														<br />
														<span className='font-weight-semibold'>
															Platnost od:
														</span>{' '}
														{item.dateFrom && (
															<FormattedDate date={item.dateFrom} />
														)}
														<br />
														<span className='font-weight-semibold'>
															Platnost do:
														</span>{' '}
														{item.dateFrom && (
															<FormattedDate date={item.dateTo} />
														)}
														<br />
														<span className='font-weight-semibold'>
															Pracoviště:
														</span>{' '}
														{item.workplace && item.workplace.name}
													</div>
												);
											})}
										</div>
									) : (
										<p>Dosud nebylo načteno</p>
									)}
								</div>
							</div>
						</div>
					</div>
				)}
				{showHealthCard && (
					<div>
						<a
							className='patient-card-detail__show_anamnestic'
							onClick={() => setShowedHealthCard(!showedHealthCard)}>
							{translation['base.shared.showMoreInfo']}
						</a>
						<div className={'collapse ' + (showedHealthCard ? 'show' : null)}>
							<PatientProfileHealthCard
								userId={user.id}
								insuranceCardDetailLink={links['insuranceCardDetail']}
							/>
						</div>
					</div>
				)}
			</div>
		</main>
	);
}
